import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';

/** Настройки → «Журнал»: append-only журнал операций (ТЗ п.8), фильтры по объекту и исполнителю. Только admin. */
type Row = { id: number; at: string; actor: string | null; role: string | null; side: string; entity: string; entityId: string; action: string; old: unknown; new: unknown; reason: string | null; ip: string | null };

export default function AuditTab() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [entity, setEntity] = useState('');
  const [actor, setActor] = useState('');
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-GB';

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = new URLSearchParams();
      if (entity) q.set('entity', entity);
      if (actor) q.set('actor', actor);
      setRows(await api.get<Row[]>(`/settings/audit?${q}`));
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [entity, actor]);
  useEffect(() => {
    void load();
  }, [load]);

  const sideLabel: Record<string, string> = { treejar: t('audit.sideTreejar'), partner: t('audit.sidePartner'), system: t('audit.sideSystem') };
  const details = (r: Row) => {
    const parts: string[] = [];
    if (r.new) parts.push(JSON.stringify(r.new));
    if (r.reason) parts.push(r.reason);
    return parts.join(' · ').slice(0, 160);
  };

  return (
    <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground-950">{t('audit.title')}</h2>
          <p className="mt-0.5 text-[11px] text-foreground-500">{t('audit.sub')}</p>
        </div>
        <div className="flex gap-2">
          <input value={entity} onChange={(e) => setEntity(e.target.value)} placeholder={t('audit.filterEntity')} className="h-8 w-[140px] rounded-md border border-background-300 bg-background-50 px-2 text-[11.5px] outline-none focus:border-primary-400" />
          <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder={t('audit.filterActor')} className="h-8 w-[160px] rounded-md border border-background-300 bg-background-50 px-2 text-[11.5px] outline-none focus:border-primary-400" />
        </div>
      </div>
      {error && (
        <p className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t('ui.error')}: {error}{' '}
          <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}
      <div className="overflow-x-auto thin-scroll">
        <table className="w-full min-w-[820px] border-collapse text-[11.5px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
              <th className="px-4 py-2 font-medium">{t('audit.when')}</th>
              <th className="px-3 py-2 font-medium">{t('audit.who')}</th>
              <th className="px-3 py-2 font-medium">{t('audit.side')}</th>
              <th className="px-3 py-2 font-medium">{t('audit.entity')}</th>
              <th className="px-3 py-2 font-medium">{t('audit.action')}</th>
              <th className="px-4 py-2 font-medium">{t('audit.details')}</th>
            </tr>
          </thead>
          <tbody>
            {!rows && !error && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-foreground-400">
                  {t('ui.loading')}
                </td>
              </tr>
            )}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-foreground-400">
                  {t('audit.empty')}
                </td>
              </tr>
            )}
            {rows?.map((r) => (
              <tr key={r.id} className="border-b border-background-200/70 last:border-0">
                <td className="tnum whitespace-nowrap px-4 py-2 text-foreground-500">{new Date(r.at).toLocaleString(locale)}</td>
                <td className="px-3 py-2 text-foreground-800">
                  {r.actor ?? '—'}
                  {r.role && <span className="ml-1 text-[10px] text-foreground-400">{r.role}</span>}
                </td>
                <td className="px-3 py-2 text-foreground-500">{sideLabel[r.side] ?? r.side}</td>
                <td className="px-3 py-2 font-mono text-[10.5px] text-foreground-700">
                  {r.entity}
                  <span className="text-foreground-400">/{r.entityId.slice(0, 8)}</span>
                </td>
                <td className="px-3 py-2 text-foreground-800">{r.action}</td>
                <td className="max-w-[360px] truncate px-4 py-2 font-mono text-[10.5px] text-foreground-500" title={details(r)}>
                  {details(r)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
